import { ArrowUpRight, Mail, MapPin, Phone } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, Reveal } from '../components/Reveal';
import { ADDRESS_LINE_1, ADDRESS_LINE_2 } from '../data/site';

const SUPPORT_EMAIL = 'support@thuladoulamassage.com';

const PRIVACY_SECTIONS = [
  {
    title: 'Information We May Collect',
    blocks: [
      { h: 'Contact Information' },
      { p: 'This may include:' },
      { list: ['Name', 'Email address', 'Mobile or telephone number', 'Mailing address', 'Billing address', 'Preferred method of communication'] },
      { h: 'Appointment and Service Information' },
      { p: 'This may include:' },
      { list: ['Appointment requests', 'Scheduled services', 'Appointment dates and times', 'Scheduling preferences', 'Rescheduling information', 'Cancellation information', 'Service preferences', 'Questions or concerns related to your appointment or services', 'Information submitted through our Contact Us page or scheduling system'] },
      { h: 'Intake and Health-Related Information' },
      { p: 'When appropriate for massage therapy, wellness services, prenatal care, postpartum services, or other services we provide, you may voluntarily provide information regarding:' },
      { list: ['Health history', 'Areas of pain or discomfort', 'Symptoms', 'Medications', 'Allergies or sensitivities', 'Pregnancy or postpartum status', 'Previous injuries or surgeries', 'Treatment goals', 'Relevant medical conditions', 'Other information necessary to safely and appropriately provide services'] },
      { p: 'Health-related information is handled in accordance with applicable privacy, professional, insurance, and recordkeeping requirements.' },
      { p: 'Where a separate privacy notice is required by law for certain health information or records, that notice will govern the information covered by it.' },
      { h: 'Payment and Transaction Information' },
      { p: 'When you make a payment, we or our authorized payment-processing providers may collect information necessary to process your transaction.' },
      { p: 'We generally do not directly store complete payment-card information when transactions are processed through an authorized third-party payment platform.' },
      { h: 'Website and Device Information' },
      { p: 'When you visit our website, certain information may be collected automatically, including:' },
      { list: ['IP address', 'Browser type', 'Device type', 'Operating system', 'Pages viewed', 'Date and time of visits', 'Website activity', 'Referring website or source', 'General website usage information'] },
      { h: 'Communication Information' },
      { p: 'We may maintain records of communications sent to or received from you through:' },
      { list: ['Telephone', 'SMS/text messaging', 'Email', 'Website forms', 'Scheduling systems', 'Intake systems', 'Customer-service or communications platforms'] },
    ],
  },
  {
    title: 'How We Use Your Information',
    blocks: [
      { p: 'We may use your information for legitimate business, operational, administrative, and service-related purposes, including to:' },
      { list: ['Schedule and manage appointments.', 'Respond to questions and service inquiries.', 'Send appointment confirmations and reminders.', 'Communicate regarding scheduling, rescheduling, or cancellations.', 'Provide customer-care and service-related communications.', 'Provide massage therapy, prenatal massage, postpartum services, therapeutic services, medical massage, and related wellness services.', 'Review intake information and prepare for services.', 'Process payments and transactions.', 'Maintain appropriate business, clinical, administrative, and service records.', 'Communicate regarding referrals, authorizations, or insurance-related administrative matters when applicable.', 'Improve our website, services, client experience, and business operations.', 'Detect or prevent fraud, unauthorized activity, misuse, or security concerns.', 'Comply with applicable laws, regulations, professional obligations, contractual requirements, and legal processes.', 'Send marketing or promotional communications only when permitted by applicable law and when appropriate consent has been obtained.'] },
    ],
  },
  {
    title: 'SMS/Text Messaging Consent',
    blocks: [
      { p: 'Thula Doula Wellness may communicate with clients and prospective clients through SMS/text messaging for customer-care, scheduling, appointment, service-related, and informational purposes.' },
      { h: 'Consent Through Website Forms' },
      { p: 'If you provide your mobile telephone number through our website, including through our Contact Us page, and submit the form, you consent to receive SMS/text messages from Thula Doula Massage LLC d/b/a Thula Doula Wellness regarding your inquiry and the services you have requested information about.' },
      { p: 'These messages may include:' },
      { list: ['Responses to your inquiry', 'Appointment scheduling', 'Appointment confirmations', 'Appointment reminders', 'Rescheduling communications', 'Cancellation notifications', 'Customer-care communications', 'Service-related updates', 'Administrative communications related to your requested services', 'Follow-up communications related to your inquiry or appointment'] },
      { p: 'By providing your mobile telephone number and submitting a website form, you consent to receive these service-related SMS/text messages from Thula Doula Wellness.' },
      { list: ['Message frequency may vary.', 'Message and data rates may apply.', 'You may reply STOP to opt out of SMS/text messages at any time.', 'You may reply HELP for assistance.', 'Consent to receive text messages is not a condition of purchasing goods or services.'] },
    ],
  },
  {
    title: 'SMS Opt-Out Requests',
    blocks: [
      { p: 'You may withdraw your consent to receive SMS/text messages at any time. You may opt out by replying:' },
      { callout: 'STOP' },
      { p: 'to a text message from Thula Doula Wellness.' },
      { p: 'After an opt-out request is received, you may receive a final confirmation message acknowledging that you have been unsubscribed.' },
      { p: 'We will also honor other reasonable requests that clearly communicate that you no longer wish to receive SMS/text messages.' },
      { p: 'Once your opt-out request has been processed, you will no longer receive SMS messages covered by that opt-out unless you later provide new consent or initiate a new conversation when permitted by law.' },
    ],
  },
  {
    title: 'SMS Help Requests',
    blocks: [
      { p: 'For assistance with SMS/text messaging, reply:' },
      { callout: 'HELP' },
      { p: 'You may also contact us directly at Phone: 832-350-8516 or Email: support@thuladoulamassage.com.' },
    ],
  },
  {
    title: 'Mobile Information and SMS Consent',
    blocks: [
      { p: 'We take the privacy of your mobile information seriously.' },
      { p: 'No mobile opt-in information or text-message consent will be shared with third parties or affiliates for their marketing or promotional purposes. Mobile telephone numbers, SMS opt-in information, and text-message consent will not be:' },
      { list: ['Sold', 'Rented', 'Traded', 'Shared for independent marketing', 'Provided to third parties or affiliates for their own promotional use'] },
      { p: 'We may use service providers that assist us with operating our communications systems, including telecommunications providers, messaging platforms, technology providers, and carriers. These providers may process information only as necessary to provide communication services on our behalf.' },
      { p: 'For example, Thula Doula Wellness may use communications providers such as RingCentral and telecommunications carriers involved in transmitting telephone calls or text messages. These providers are not authorized by Thula Doula Wellness to use your SMS consent for their own marketing purposes.' },
    ],
  },
  {
    title: 'Marketing and Promotional Communications',
    blocks: [
      { p: 'Service-related SMS consent is separate from consent to receive promotional or marketing communications.' },
      { p: 'Submitting a Contact Us form or providing your telephone number for appointment-related purposes does not automatically enroll you in promotional SMS marketing.' },
      { p: 'Where required, promotional or marketing text messages will be sent only after appropriate consent has been obtained.' },
      { p: 'You may withdraw consent to receive promotional communications at any time. Opting out of promotional messages does not necessarily prevent Thula Doula Wellness from sending necessary non-marketing communications regarding an existing appointment, transaction, inquiry, or service request when permitted by law.' },
    ],
  },
  {
    title: 'Email Communications',
    blocks: [
      { p: 'We may use your email address to:' },
      { list: ['Respond to inquiries', 'Send appointment information', 'Send forms or intake documents', 'Provide scheduling updates', 'Communicate regarding services', 'Send administrative or account-related information', 'Send promotional communications when permitted'] },
      { p: 'Marketing emails may contain an unsubscribe option when required.' },
    ],
  },
  {
    title: 'Cookies and Website Technologies',
    blocks: [
      { p: 'Our website may use cookies and similar technologies to:' },
      { list: ['Support website functionality', 'Remember website preferences', 'Analyze website activity', 'Understand visitor behavior', 'Improve website performance', 'Maintain security', 'Measure website traffic'] },
      { p: 'You may control or disable cookies through your browser settings. Disabling certain cookies may affect website functionality.' },
    ],
  },
  {
    title: 'How We Share Information',
    blocks: [
      { p: 'We do not sell or rent personal information to third parties. We may disclose personal information when reasonably necessary to operate our business or provide services.' },
      { h: 'Service Providers' },
      { p: 'We may use trusted service providers for functions such as:' },
      { list: ['Scheduling', 'Appointment management', 'SMS/text messaging', 'Telephone communications', 'Email delivery', 'Website hosting', 'Payment processing', 'Data storage', 'Information technology', 'Analytics', 'Insurance-related administrative services', 'Business operations'] },
      { p: 'Service providers may receive only the information reasonably necessary to perform services on our behalf.' },
      { h: 'Healthcare, Referral, or Administrative Coordination' },
      { p: 'When applicable and permitted by law, information may be disclosed to entities involved in coordinating services, referrals, authorizations, benefits, or other administrative matters.' },
      { h: 'Legal Requirements' },
      { p: 'We may disclose information when we reasonably believe disclosure is necessary to:' },
      { list: ['Comply with applicable law', 'Respond to a lawful subpoena, court order, or governmental request', 'Protect our legal rights', 'Protect the safety of clients, staff, or others', 'Prevent fraud or unlawful activity', 'Investigate suspected misuse or security concerns'] },
      { h: 'Business Transactions' },
      { p: 'If Thula Doula Wellness is involved in a merger, acquisition, restructuring, financing, or sale of business assets, personal information may be transferred as part of that transaction in accordance with applicable law.' },
    ],
  },
  {
    title: 'How We Protect Your Information',
    blocks: [
      { p: 'Thula Doula Wellness uses reasonable administrative, physical, and technical safeguards designed to protect personal information from:' },
      { list: ['Unauthorized access', 'Misuse', 'Disclosure', 'Alteration', 'Loss', 'Destruction'] },
      { p: 'Access to personal information is limited to team members, contractors, and service providers who require the information for legitimate business or service-related purposes.' },
      { p: 'However, no website, electronic communication, data-storage system, telephone network, or internet transmission can be guaranteed to be completely secure.' },
    ],
  },
  {
    title: 'Data Retention',
    blocks: [
      { p: 'We retain personal information only for as long as reasonably necessary for purposes such as:' },
      { list: ['Providing services', 'Maintaining appointment records', 'Maintaining clinical or service records', 'Processing transactions', 'Meeting insurance or authorization requirements', 'Complying with legal and regulatory requirements', 'Resolving disputes', 'Maintaining financial records', 'Preventing fraud', 'Enforcing agreements', 'Supporting business operations'] },
      { p: 'Retention periods may vary depending on the type of information and applicable requirements.' },
    ],
  },
  {
    title: 'Your Privacy Choices',
    blocks: [
      { p: 'Depending on applicable law, you may request to:' },
      { list: ['Review certain personal information we maintain about you', 'Correct inaccurate information', 'Update your contact information', 'Request deletion of eligible information', 'Change your communication preferences', 'Opt out of promotional communications', 'Opt out of SMS/text messaging'] },
      { p: 'Certain records may need to be retained despite a deletion request when required by law, insurance requirements, contractual obligations, clinical documentation standards, professional requirements, or legitimate business recordkeeping needs.' },
    ],
  },
  {
    title: "Children's Privacy",
    blocks: [
      { p: 'Our website is not intended to knowingly collect personal information directly from children without authorization from a parent, guardian, or other legally authorized individual when such authorization is required.' },
    ],
  },
  {
    title: 'Third-Party Websites and Services',
    blocks: [
      { p: 'Our website may contain links to third-party services, including:' },
      { list: ['Scheduling platforms', 'Payment processors', 'Communications platforms', 'Insurance or referral resources', 'Other external websites'] },
      { p: 'Those organizations maintain their own privacy practices and policies.' },
      { p: 'Thula Doula Wellness is not responsible for the privacy practices of independently operated third-party websites or services.' },
    ],
  },
  {
    title: 'Changes to This Privacy Policy',
    blocks: [
      { p: 'We may update this Privacy Policy periodically to reflect changes in:' },
      { list: ['Our services', 'Our business practices', 'Technology', 'Communications systems', 'Legal requirements', 'Regulatory requirements'] },
      { p: 'The updated Privacy Policy will be posted on our website with a revised effective date. Material changes may also be communicated through other appropriate methods.' },
    ],
  },
];

const SMS_SECTIONS = [
  {
    title: 'SMS Program Description',
    blocks: [
      { p: 'Thula Doula Wellness uses SMS/text messaging to communicate with clients and prospective clients regarding services they have requested or inquiries they have submitted. Messages may include:' },
      { list: ['Responses to inquiries', 'Appointment scheduling', 'Appointment confirmations', 'Appointment reminders', 'Rescheduling communications', 'Cancellation notifications', 'Customer-care communications', 'Service-related notifications', 'Administrative communications', 'Follow-up communications related to requested services'] },
      { p: 'Marketing or promotional messages may be sent only when appropriate consent has been obtained.' },
    ],
  },
  {
    title: 'Consent to Receive SMS Messages',
    blocks: [
      { p: 'By providing your mobile telephone number to Thula Doula Wellness and submitting a form on our website, including our Contact Us form, you consent to receive SMS/text messages related to your inquiry, appointments, scheduling, customer care, and requested services.' },
      { p: 'You may also consent through other permitted methods, including initiating a text-message conversation with us or otherwise requesting communication by text.' },
      { p: 'Consent to receive SMS/text messages is not a condition of purchasing goods or services.' },
    ],
  },
  {
    title: 'Website Contact Form Consent Statement',
    blocks: [
      { p: 'When a mobile telephone number is provided through our website, the following consent applies:' },
      { consent:
          'By providing your mobile phone number and submitting this form, you consent to receive SMS/text messages from Thula Doula Massage LLC d/b/a Thula Doula Wellness regarding your inquiry, appointments, scheduling, rescheduling, cancellations, customer care, and other service-related communications. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for assistance. Consent to receive text messages is not a condition of purchasing goods or services. No mobile opt-in information or text-message consent will be shared with third parties or affiliates for marketing or promotional purposes.' },
    ],
  },
  {
    title: 'Message Frequency',
    blocks: [
      { p: 'Message frequency may vary. The number of messages you receive may depend on:' },
      { list: ['The nature of your inquiry', 'Your scheduled appointments', 'Appointment changes', 'Customer-care needs', 'Requested services', 'Administrative communications'] },
    ],
  },
  {
    title: 'Message and Data Rates',
    blocks: [
      { p: 'Message and data rates may apply. Your wireless provider may charge fees for sending or receiving text messages according to your mobile service plan.' },
      { p: 'Thula Doula Wellness is not responsible for fees imposed by your wireless carrier.' },
    ],
  },
  {
    title: 'How to Opt Out',
    blocks: [
      { p: 'You may stop receiving SMS/text messages by replying:' },
      { callout: 'STOP' },
      { p: 'to a text message from Thula Doula Wellness. You may receive a final confirmation message acknowledging your opt-out.' },
      { p: 'We will also honor other reasonable requests that clearly communicate that you no longer wish to receive text messages.' },
    ],
  },
  {
    title: 'How to Get Help',
    blocks: [
      { p: 'For assistance, reply:' },
      { callout: 'HELP' },
      { p: 'You may also contact us at Phone: 832-350-8516 or Email: support@thuladoulamassage.com.' },
    ],
  },
  {
    title: 'Mobile Information and Consent',
    blocks: [
      { p: 'No mobile opt-in information or SMS/text-message consent will be shared with third parties or affiliates for marketing or promotional purposes. We do not sell, rent, or trade SMS consent information.' },
      { p: 'Service providers involved in transmitting messages may process mobile information only as necessary to provide communication services on our behalf.' },
    ],
  },
  {
    title: 'Changing Your Telephone Number',
    blocks: [
      { p: 'If your mobile telephone number changes, please notify Thula Doula Wellness so that we can update our records.' },
      { p: 'You are responsible for ensuring that the number you provide belongs to you or that you are otherwise authorized to use that number.' },
    ],
  },
  {
    title: 'Message Delivery',
    blocks: [
      { p: 'SMS/text-message delivery may be affected by circumstances outside our control, including:' },
      { list: ['Wireless carrier availability', 'Network outages', 'Device settings', 'Internet availability', 'Telecommunications routing', 'Service interruptions'] },
      { p: 'Thula Doula Wellness cannot guarantee that every message will be delivered or received within a specific period.' },
    ],
  },
  {
    title: 'Privacy',
    blocks: [
      { p: 'Your privacy is important to us. Our collection, use, storage, and disclosure of personal information is governed by the Thula Doula Wellness Privacy Policy above.' },
      { p: 'No mobile opt-in information or text-message consent will be shared with third parties or affiliates for their marketing or promotional purposes.' },
    ],
  },
  {
    title: 'Changes to These SMS Terms',
    blocks: [
      { p: 'We may update these SMS Terms periodically to reflect changes in:' },
      { list: ['Communications practices', 'Services', 'Technology', 'Legal requirements', 'Regulatory requirements'] },
      { p: 'Updated terms will be posted on our website with a revised effective date.' },
    ],
  },
];

function Blocks({ blocks }) {
  return blocks.map((b, i) => {
    if (b.h) return <h3 key={i} className="mt-7 text-base font-bold text-brand-ink">{b.h}</h3>;
    if (b.list)
      return (
        <ul key={i} className="mt-4 max-w-3xl space-y-2">
          {b.list.map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-brand-moss md:text-base">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-sage" aria-hidden="true" />
              {item}
            </li>
          ))}
        </ul>
      );
    if (b.callout)
      return (
        <span
          key={i}
          className="mt-5 inline-flex items-center rounded-xl bg-brand-ink px-5 py-2.5 font-mono text-lg font-bold tracking-widest text-brand-sage"
        >
          {b.callout}
        </span>
      );
    if (b.consent)
      return (
        <blockquote
          key={i}
          className="mt-5 max-w-3xl rounded-2xl border-l-4 border-brand-teal bg-brand-parchment p-6 text-sm italic leading-relaxed text-brand-ink"
        >
          {b.consent}
        </blockquote>
      );
    return (
      <p key={i} className="mt-4 max-w-3xl text-sm leading-relaxed text-brand-moss md:text-base">
        {b.p}
      </p>
    );
  });
}

function Section({ num, title, blocks, anchorId }) {
  return (
    <Reveal>
      <article id={anchorId} className="border-b border-brand-ink/10 py-10 md:py-12">
        <h2 className="font-display text-3xl font-light tracking-tight text-brand-ink md:text-4xl">
          <span className="mr-3 text-lg italic text-brand-teal/60">{num}</span>
          {title}
        </h2>
        <div className="mt-2">
          <Blocks blocks={blocks} />
        </div>
      </article>
    </Reveal>
  );
}

export default function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy Policy | Thula Doula Wellness"
        description="Thula Doula Massage LLC d/b/a Thula Doula Wellness Privacy Policy and SMS/Text Messaging Terms of Service — how we collect, use, and protect your information."
      />
      <PageHeader
        testId="privacy-page-header"
        eyebrow="Legal"
        lines={['Privacy Policy.']}
        sub="Thula Doula Massage LLC, doing business as Thula Doula Wellness, respects your privacy and is committed to protecting the personal information you share with us."
        cta={false}
      />

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="privacy-section">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <div className="rounded-2xl bg-brand-parchment px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-brand-teal" data-testid="privacy-effective-date">
              Effective Date: September 25, 2026
            </div>
          </Reveal>
          <Reveal>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-brand-moss md:text-base">
              Thula Doula Massage LLC, doing business as Thula Doula Wellness (“Thula Doula
              Wellness,” “we,” “us,” or “our”), respects your privacy and is committed to protecting
              the personal information you share with us.
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-brand-moss md:text-base">
              This Privacy Policy explains what information we may collect through our website,
              contact forms, scheduling systems, intake forms, telephone calls, text messages, email
              communications, payment systems, and services; how we use that information; how it may
              be shared; and the choices available to you.
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-brand-moss md:text-base">
              By using our website, submitting a form, scheduling services, providing information to
              us, or otherwise communicating with Thula Doula Wellness, you acknowledge the
              practices described in this Privacy Policy.
            </p>
          </Reveal>

          <div className="mt-14">
            {PRIVACY_SECTIONS.map((s, i) => (
              <Section key={s.title} num={`${i + 1}.`} title={s.title} blocks={s.blocks} anchorId={`privacy-${i + 1}`} />
            ))}
          </div>

          <div className="mt-24" data-testid="sms-terms">
            <MaskedLinesOnView
              as="h2"
              lines={['SMS/Text Messaging Terms of Service']}
              className="font-display text-[clamp(1.9rem,4vw,3.25rem)] font-light leading-[1.1] tracking-tight text-brand-ink"
            />
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-brand-teal">
              Effective Date: September 25, 2026
            </p>
            <p className="mt-6 max-w-3xl text-sm leading-relaxed text-brand-moss md:text-base">
              These SMS/Text Messaging Terms of Service apply to text messages sent by or on behalf
              of Thula Doula Massage LLC d/b/a Thula Doula Wellness.
            </p>
            <div className="mt-10">
              {SMS_SECTIONS.map((s, i) => (
                <Section key={s.title} num={`${i + 1}.`} title={s.title} blocks={s.blocks} anchorId={`sms-${i + 1}`} />
              ))}
            </div>
          </div>

          <Reveal>
            <div data-testid="privacy-contact" className="mt-20 rounded-3xl bg-brand-ink p-8 text-brand-cream md:p-12">
              <MaskedLinesOnView as="h2" lines={['Contact Us']} className="font-display text-3xl font-light tracking-tight md:text-4xl" />
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-brand-cream/75 md:text-base">
                For questions, privacy requests, or concerns regarding this Privacy Policy, please
                contact:
              </p>
              <div className="mt-7 space-y-4 text-sm text-brand-cream/80 md:text-base">
                <p>
                  Thula Doula Massage LLC
                  <br />
                  d/b/a Thula Doula Wellness
                </p>
                <p className="flex items-start gap-3">
                  <MapPin className="mt-1 size-4 shrink-0 text-brand-sage/70" />
                  <span>
                    {ADDRESS_LINE_1}
                    <br />
                    {ADDRESS_LINE_2}
                  </span>
                </p>
                <a
                  data-testid="privacy-phone-link"
                  href="tel:+18323508516"
                  className="flex items-center gap-3 transition-colors duration-300 hover:text-brand-sage"
                >
                  <Phone className="size-4 text-brand-sage/70" /> 832-350-8516
                </a>
                <a
                  data-testid="privacy-email-link"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="flex items-center gap-3 transition-colors duration-300 hover:text-brand-sage"
                >
                  <Mail className="size-4 text-brand-sage/70" /> {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-12 text-center">
              <a
                data-testid="privacy-book-btn"
                href="https://thuladoulamassage.janeapp.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-8 py-4 text-sm font-semibold text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink active:scale-[0.97]"
              >
                Book a Massage
                <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
